import {AssumeRoleWithWebIdentityCommand, STSClient} from '@aws-sdk/client-sts';
import type {TemplateInput} from '@amzn/mulberry-internal-typescript-client';
import {GetTemplateCommand, MulberryInternal} from '@amzn/mulberry-internal-typescript-client';

import {isLocalHost} from 'src/utils/utils';

interface PreviewConfig {
  roleArn: string;
  apiEndpoint: string;
  region: string;
}

type STAGE = 'Dev' | 'Beta' | 'Gamma' | 'Prod';
const STAGE_MAP_LOOKUP: Record<string, STAGE> = {  dev: 'Dev', beta: 'Beta', gamma: 'Gamma', prod: 'Prod'};
const REGION = 'us-east-1';
const MIDWAY_TOKEN_KEY = 'midway_id_token';
const CREDENTIALS_KEY = 'preview_credentials';
const CREDENTIALS_TTL = 50 * 60 * 1000; // 50 minutes (credentials last 1 hour, refresh early)

const resolveStage = (): STAGE => {
  const stage = 'sojask';
  return STAGE_MAP_LOOKUP[stage.toLowerCase()] || 'Dev';
};

const  buildApiEndpoint = () => {
  const stage = resolveStage();
  return `https://mulberry-internal.${stage}.mulberry.music.amazon.dev`
}

const buildRoleArn = () => {
  // const stage = resolveStage();
  return `arn:aws:iam::354918370639:role/sojask-mulberry-MulberryBurberryPreviewRead`
}


const PREVIEW_CONFIG: Record<string, PreviewConfig> = {
  Dev: {
    roleArn: buildRoleArn(),
    apiEndpoint: buildApiEndpoint(),
    region: REGION,
  },
  Beta: {
    roleArn: buildRoleArn(),
    apiEndpoint: buildApiEndpoint(),
    region: REGION,
  },
  Gamma: {
    roleArn: buildRoleArn(),
    apiEndpoint: buildApiEndpoint(),
    region: REGION,
  },
  Prod: {
    roleArn: buildRoleArn(),
    apiEndpoint: buildApiEndpoint(),
    region: REGION,
  },
};

const getMidwayToken = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get('id_token');
  if (tokenFromUrl) {
    sessionStorage.setItem(MIDWAY_TOKEN_KEY, tokenFromUrl);
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('id_token');
    window.history.replaceState({}, '', cleanUrl.toString());
    return tokenFromUrl;
  }
  return sessionStorage.getItem(MIDWAY_TOKEN_KEY);
};

const redirectToMidway = (): never => {
  const clientId = window.location.hostname;
  const redirectUri = window.location.href;
  const nonce = crypto.randomUUID();
  window.location.href = `https://midway-auth.amazon.com/SSO/redirect?response_type=id_token&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=openid&nonce=${nonce}`;
  throw new Error('Redirecting to Midway for authentication');
};

const getCachedCredentials = () => {
  const cached = sessionStorage.getItem(CREDENTIALS_KEY);
  if (!cached) return null;
  const { credentials, expiry } = JSON.parse(cached);
  if (Date.now() > expiry) {
    sessionStorage.removeItem(CREDENTIALS_KEY);
    return null;
  }
  return credentials;
};

const cacheCredentials = (credentials: { accessKeyId: string; secretAccessKey: string; sessionToken: string }) => {
  sessionStorage.setItem(CREDENTIALS_KEY, JSON.stringify({
    credentials,
    expiry: Date.now() + CREDENTIALS_TTL,
  }));
};

const getPreviewCredentials = async (config: PreviewConfig) => {
  const cached = getCachedCredentials();
  if (cached) return cached;

  const token = getMidwayToken();
  if (!token) {
    return redirectToMidway();
  }

  const stsClient = new STSClient({ region: config.region });
  const command = new AssumeRoleWithWebIdentityCommand({
    RoleArn: config.roleArn,
    RoleSessionName: 'burberry-preview',
    WebIdentityToken: token,
  });

  let response;
  try {
    response = await stsClient.send(command);
  } catch (error: any) {
    if (error.name === 'ExpiredTokenException' || error.name === 'InvalidIdentityTokenException') {
      sessionStorage.removeItem(MIDWAY_TOKEN_KEY);
      sessionStorage.removeItem(CREDENTIALS_KEY);
      redirectToMidway();
    }
    throw error;
  }

  if (!response.Credentials) {
    throw new Error('STS did not return credentials');
  }

  const credentials = {
    accessKeyId: response.Credentials.AccessKeyId!,
    secretAccessKey: response.Credentials.SecretAccessKey!,
    sessionToken: response.Credentials.SessionToken!,
  };
  cacheCredentials(credentials);
  return credentials;
};

const fetchViaLocalProxy = async (siteId: string, version: string): Promise<TemplateInput> => {
  const response = await fetch(`/api/preview/template/${siteId}/${version}`);
  if (!response.ok) {
    throw new Error(`Local preview proxy failed: ${response.status}`);
  }
  const data = await response.json();
  return (data.template?.templateInput ?? data) as TemplateInput;
};

const fetchViaSigV4 = async (siteId: string, version: string): Promise<TemplateInput> => {
  const stage = resolveStage();
  const config = PREVIEW_CONFIG[stage] || PREVIEW_CONFIG.Beta;

  const credentials = await getPreviewCredentials(config);

  const client = new MulberryInternal({
    region: config.region,
    endpoint: config.apiEndpoint,
    credentials,
    signingName: 'execute-api',
  } as any);

  const response = await client.send(new GetTemplateCommand({
    siteId,
    versionKey: version,
  }));

  const templateInput = response.template?.templateInput;
  if (!templateInput) {
    throw new Error(`Template response missing templateInput for site: ${siteId}, version: ${version}`);
  }
  return templateInput;
};

export const fetchTemplateFromPreviewAPI = async (
  siteId: string,
  version: string,
): Promise<TemplateInput> => {
  if (isLocalHost()) {
    return fetchViaLocalProxy(siteId, version);
  }
  return fetchViaSigV4(siteId, version);
};
